import { useState } from "react";
import { Link } from "react-router-dom";
import { useFetch } from "../lib/api";
import { todayISO, addDays, toISO } from "../lib/fmt";
import { Page, Spinner, ErrorBox, Card, AccountLink } from "./ui";

const back = (n) => toISO(addDays(new Date(), -n));

function Stat({ label, value, hint, to, testid }) {
  const body = (
    <div className="card h-full p-4 transition-transform hover:-translate-y-0.5" data-testid={testid}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-3xl font-bold text-[#132F3A]">{value ?? "Non disponible"}</div>
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function Watch({ title, items, render, testid, empty }) {
  return (
    <Card title={`${title} (${items.length})`} className="h-full">
      <ul className="space-y-2 text-sm" data-testid={testid}>
        {items.length === 0 && <li className="text-slate-400">{empty}</li>}
        {items.map(render)}
      </ul>
    </Card>
  );
}

export default function Dashboard() {
  const [preset, setPreset] = useState("30");
  const [range, setRange] = useState({ start: back(30), end: todayISO() });
  const { data, error } = useFetch(`/admin/dashboard?start=${range.start}&end=${range.end}`);
  const pick = (p) => { setPreset(p); if (p !== "custom") setRange({ start: back(Number(p)), end: todayISO() }); };
  return (
    <Page title="Tableau de bord" subtitle="Indicateurs calculés à partir des données réelles de la plateforme." testid="admin-dashboard">
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {[["7", "7 jours"], ["30", "30 jours"], ["custom", "Personnalisée"]].map(([k, l]) => (
          <button key={k} onClick={() => pick(k)} className={`rounded-full px-4 py-2 text-sm font-semibold ${preset === k ? "bg-[#24616A] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`} data-testid={`period-${k}`}>{l}</button>
        ))}
        {preset === "custom" && (
          <div className="flex items-center gap-2 text-sm">
            <input type="date" className="field !min-h-[40px] !w-auto" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="period-start" />
            <span>→</span>
            <input type="date" className="field !min-h-[40px] !w-auto" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="period-end" />
          </div>
        )}
      </div>
      {error ? <ErrorBox error={error} /> : !data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Comptes" value={data.total} to="/admin/comptes" testid="stat-total" />
            <Stat label="Nouveaux comptes" value={data.new} hint="sur la période" testid="stat-new" />
            <Stat label="Comptes actifs" value={data.active} hint="action utile sur la période" testid="stat-active" />
            <Stat label="Sans planning publié" value={data.no_published} to="/admin/comptes" testid="stat-no-published" />
            <Stat label="Invitations en attente" value={data.invites_pending} testid="stat-invites" />
            <Stat label="Assistance ouverte" value={data.tickets_open} to="/admin/assistance" testid="stat-tickets" />
            <Stat label="Contact facturation" value={data.billing_open} to="/admin/assistance?category=billing" testid="stat-billing" />
          </div>
          <p className="mt-3 text-xs text-slate-400">Compte actif : au moins une disponibilité encodée, une affectation créée ou modifiée, ou un planning publié pendant la période. Une connexion seule ne compte pas.</p>
          <h2 className="mb-3 mt-10 text-lg font-bold">À suivre</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <Watch title="Démarrage incomplet" items={data.watch.onboarding} testid="watch-onboarding" empty="Aucun compte"
              render={(a) => <li key={a.id}><AccountLink id={a.id} name={a.name} /> <span className="text-slate-400">· {a.owner_name}</span></li>} />
            <Watch title={`Inactifs depuis ${data.inactivity_days} j`} items={data.watch.inactive} testid="watch-inactive" empty="Aucun compte"
              render={(a) => <li key={a.id}><AccountLink id={a.id} name={a.name} /> <span className="text-slate-400">· {a.owner_name}</span></li>} />
            <Watch title="Demandes en attente de réponse" items={data.watch.tickets} testid="watch-tickets" empty="Aucune demande"
              render={(t) => <li key={t.id}><Link to={`/admin/assistance/${t.id}`} className="font-semibold text-teal-800 hover:underline">{t.subject}</Link> <span className="text-slate-400">· {t.account}</span></li>} />
            <Watch title="Actions de suivi échues" items={data.watch.followups} testid="watch-followups" empty="Aucune action"
              render={(a) => <li key={a.id}><AccountLink id={a.id} name={a.name} /></li>} />
          </div>
        </>
      )}
    </Page>
  );
}
